<script lang="ts">
	// The design system's Row, the other half of TablePanel's --cols contract
	// (design/components/surfaces/TablePanel.jsx ships them together). A row
	// takes its columns from the panel it sits in and never sets its own.
	//
	// It owns the three row states, so none of them is a page's style:
	//
	//   idle         nothing.
	//   unconfirmed  the server has not agreed yet: a 2px amber leading edge,
	//                and the row still shows the new value.
	//   marked       it just landed: a green wash that fades (bosla-mark).
	//
	// The leading edge is reserved at 2px in every state, so a row that turns
	// unconfirmed does not shift sideways.
	//
	// The design's Row takes `as`, and so does this one: as="button" makes the
	// whole row one tap, as Help's walkthroughs and scenes are (D22). It then
	// presses as a bare control does (interaction.css), and `current` marks the
	// one row that is on now, such as the scene playing, on the card header's
	// ground, as the D22 page draws it.

	import type { Snippet } from 'svelte';
	import { pressable } from './pressable';

	interface Props {
		state?: 'idle' | 'unconfirmed' | 'marked';
		/** Passed through, as the design's Row does: a group's heading row takes the header ground. */
		style?: string;
		testId?: string;
		/** The design's `as`: a div, or one button for the whole row. */
		as?: 'div' | 'button';
		/** What a tap on a row that is a button does. */
		onPress?: () => void;
		/** The row that is on now, of a list that has one: said aloud and drawn on its own ground. */
		current?: boolean;
		children: Snippet;
	}

	let {
		state = 'idle',
		style,
		testId,
		as = 'div',
		onPress,
		current = false,
		children,
	}: Props = $props();
</script>

{#if as === 'button'}
	<button
		type="button"
		class="row tap"
		class:unconfirmed={state === 'unconfirmed'}
		class:current
		data-row
		data-row-marked={state === 'marked' ? '' : undefined}
		data-press="bare"
		aria-current={current ? 'true' : undefined}
		data-testid={testId}
		{style}
		onclick={onPress}
		{@attach pressable}
	>
		{@render children()}
	</button>
{:else}
	<div
		class="row"
		class:unconfirmed={state === 'unconfirmed'}
		data-row
		data-row-marked={state === 'marked' ? '' : undefined}
		data-testid={testId}
		{style}
	>
		{@render children()}
	</div>
{/if}

<style>
	.row {
		display: grid;
		grid-template-columns: var(--cols);
		gap: var(--col-gap);
		align-items: center;
		padding: var(--row-pad);
		border-block-end: 1px solid var(--border-inset);
		border-inline-start: 2px solid transparent;
	}

	.row.unconfirmed {
		border-inline-start-color: var(--signal-flag);
	}

	/* A button keeps the row's look: no ground, no edge of its own, the
	   page's type, and the whole width. */
	.tap {
		box-sizing: border-box;
		inline-size: 100%;
		margin: 0;
		border-block-start: none;
		border-inline-end: none;
		background: transparent;
		color: inherit;
		font: inherit;
		text-align: start;
		cursor: pointer;
	}

	.tap.current {
		background: var(--surface-card-header);
	}
</style>
